// Prayer times API service
import axios from "axios";
import { XMLParser } from "fast-xml-parser";
import * as cheerio from "cheerio";
import { fallbackPrayerTimes } from "./prayerTimes/index";
import React from 'react';
import type { PrayerSource } from "../components/PrayerSourceSelector";

// Dev proxy helpers - routes requests through Vite proxy to bypass CORS
const isDev = import.meta.env.DEV;
const islamicBaseUrl = isDev ? '/api/islamiska' : 'https://www.islamiskaforbundet.se';
const vaktijaBaseUrl = isDev ? '/api/vaktija' : 'https://vaktija.eu';
const aladhanBaseUrl = isDev ? '/api/aladhan' : 'https://api.aladhan.com';

// Lägg till en flagga för att kontrollera om vi ska visa alla felmeddelanden eller inte
let verboseLogging = false;

// Funktion för att tyst logga fel - loggar bara en gång per session
const loggedErrors = new Set<string>();
export function quietLog(errorType: string, error: any): void {
  // Skapa en nyckel för denna feltyp
  const errorKey = `${errorType}`;

  // Logga bara om vi inte redan loggat detta fel eller om verbose loggning är på
  if (verboseLogging || !loggedErrors.has(errorKey)) {
    console.error(`${errorType}: ${error}`);
    loggedErrors.add(errorKey);
  }
}

interface PrayerTime {
  name: string;
  time: string;
  icon?: React.ReactNode;
  isNext?: boolean;
}

// Add nextDayPrayerTimes to the PrayerTimesResponse interface
// Fix the PrayerTimesResponse interface to match the actual response structure
export interface PrayerTimesResponse {
  prayerTimes: {
    fajr: string;
    sunrise?: string;
    shoruk?: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
    midnight?: string;
  };
  date: string;
  location: string;
  nextDayPrayerTimes?: {
    fajr: string;
    sunrise?: string;
    shoruk?: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
    midnight?: string;
  };
}

// City mapping for Islamiska Förbundet API
export const cityMapping: Record<string, string> = {
  "Stockholm": "Stockholm, SE",
  "Göteborg": "Göteborg, SE",
  "Malmö": "Malmö, SE",
  "Uppsala": "Uppsala, SE",
  "Västerås": "Västerås, SE",
  "Örebro": "Örebro, SE",
  "Linköping": "Linköping, SE",
  "Helsingborg": "Helsingborg, SE",
  "Jönköping": "Jönköping, SE",
  "Norrköping": "Norrköping, SE",
  // Lägg till fler städer efter behov
};

// City mapping for Vaktija.eu
export const vaktijaCityMapping: Record<string, string> = {
  "Stockholm": "stockholm",
  "Göteborg": "goeteborg",
  "Malmö": "malmoe",
  "Borås": "boras",
  "Gislaved": "gislaved",
  "Halmstad": "halmstad",
  "Helsingborg": "helsingborg",
  "Jönköping": "joenkoeping",
  "Kalmar": "kalmar",
  "Landskrona": "landskrona",
  "Linköping": "linkoeping",
  "Norrköping": "norrkoeping",
  "Örebro": "oerebro",
  "Oskarshamn": "oskarshamn",
  "Skövde": "skovde",
  "Trelleborg": "trelleborg",
  "Trollhättan": "trollhaettan",
  "Västerås": "vaesteras",
  "Växjö": "vaexjoe",
  "Vetlanda": "vetlanda",
};

// Interfaces för API-svar
interface AladhanApiResponse {
  code: number;
  status: string;
  data: {
    timings: {
      Fajr: string;
      Sunrise: string;
      Dhuhr: string;
      Asr: string;
      Maghrib: string;
      Isha: string;
      Midnight: string;
    };
    date: {
      readable: string;
    };
  };
}

interface IslamiskForbundetApiResponse {
  times: {
    fajr: string;
    sunrise: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
  };
}

interface PrayerTimesXmlResponse {
  prayer_times: {
    day: Array<{
      $: {
        day_number: string;
      };
      fajr: string[];
      sunrise: string[];
      dhuhr: string[];
      asr: string[];
      maghrib: string[];
      isha: string[];
    }>;
  };
}

// This function fetches prayer times from Islamiska Förbundet i Sverige (IFiS)
const getPrayerTimes = async (
  location: string,
  dateStr?: string,
): Promise<PrayerTimesResponse> => {
  // Parse the date from string or use current date
  const currentDate = dateStr ? new Date(dateStr) : new Date();
  const day = currentDate.getDate();
  const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
  const year = currentDate.getFullYear();

  // Använd först de fasta tiderna om de finns för denna månad
  if (fallbackPrayerTimes[month] && fallbackPrayerTimes[month][day]) {
    const fixedTimes = fallbackPrayerTimes[month][day];
    return {
      date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
      location: location,
      prayerTimes: {
        fajr: fixedTimes.fajr,
        shoruk: fixedTimes.shoruk,
        sunrise: fixedTimes.shoruk,
        dhuhr: fixedTimes.dhuhr,
        asr: fixedTimes.asr,
        maghrib: fixedTimes.maghrib,
        isha: fixedTimes.isha,
      },
    };
  }

  // Extract city name from location string
  const cityName = location.split(",")[0].trim();
  const cityCode = cityMapping[cityName] || "stockholm";

  try {
    // Skicka anropet UTAN &year= parametern eftersom deras server numera kraschar/redirectar till 404 när den inkluderas
    // City code måste ha formatet "Stockholm, SE", inte små bokstäver.
    const url = `${islamicBaseUrl}/bonetider/?city=${encodeURIComponent(cityCode)}&month=${month}`;

    const response = await axios.get(url, { timeout: 10000 });
    const $ = cheerio.load(response.data as string);

    // Hitta tabellraden för den aktuella dagen
    let prayerTimesRow: any = null;

    // Sök igenom alla tabellrader för att hitta den med rätt dag
    $('table tr').each((_index, element) => {
      const firstCell = $(element).find('td').first().text().trim();
      if (firstCell === day.toString()) {
        prayerTimesRow = element;
        return false; // Avbryt loopen när vi hittat rätt rad
      }
    });

    if (prayerTimesRow) {
      // Extrahera bönetiderna från raden
      const cells = $(prayerTimesRow).find('td');

      if (cells.length >= 7) { // Dag + 6 bönetider
        return {
          date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
          location: location,
          prayerTimes: {
            fajr: $(cells[1]).text().trim(),
            shoruk: $(cells[2]).text().trim(),
            sunrise: $(cells[2]).text().trim(),
            dhuhr: $(cells[3]).text().trim(),
            asr: $(cells[4]).text().trim(),
            maghrib: $(cells[5]).text().trim(),
            isha: $(cells[6]).text().trim(),
          },
        };
      }
    }

    throw new Error("Kunde inte hitta bönetider för den valda dagen");
  } catch (scrapingError) {
    quietLog("Fel vid skrapning av bönetider", scrapingError);

    // Prova API-metoden
    try {
      // Använder Islamiska Förbundets officiella bönetider API
      const apiUrl = `${islamicBaseUrl}/bonetider/api/v1/times?city=${cityCode}&date=${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

      const apiResponse = await axios.get<IslamiskForbundetApiResponse>(apiUrl, { timeout: 5000 });

      // Kontrollera om vi fick ett giltigt svar
      if (apiResponse.data && apiResponse.data.times) {
        return {
          date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
          location: location,
          prayerTimes: {
            fajr: apiResponse.data.times.fajr,
            shoruk: apiResponse.data.times.sunrise,
            sunrise: apiResponse.data.times.sunrise,
            dhuhr: apiResponse.data.times.dhuhr,
            asr: apiResponse.data.times.asr,
            maghrib: apiResponse.data.times.maghrib,
            isha: apiResponse.data.times.isha,
          },
        };
      }

      throw new Error("Kunde inte tolka data från API");
    } catch (apiError) {
      quietLog("Fel vid hämtning från API", apiError);

      // Prova den gamla XML-metoden
      try {
        const xmlUrl = `${islamicBaseUrl}/wp-content/plugins/bonetider/Bonetider_Widget_Public/includes/get_monthly_prayer_times.php?city=${cityCode}&month=${month}&year=${year}&calculation_method=mwl`;

        const xmlResponse = await axios.get(xmlUrl, { timeout: 5000 });

        // Använd fast-xml-parser istället för xml2js
        const parser = new XMLParser({
          ignoreAttributes: false,
          attributeNamePrefix: ""
        });

        const prayerTimesData = parser.parse(xmlResponse.data as string) as PrayerTimesXmlResponse;

        // Extract the prayer times for the current day
        if (prayerTimesData && prayerTimesData.prayer_times && prayerTimesData.prayer_times.day) {
          const dayData = prayerTimesData.prayer_times.day.find(
            (d) => parseInt(d.$.day_number) === day
          );

          if (dayData) {
            return {
              date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
              location: location,
              prayerTimes: {
                fajr: dayData.fajr[0],
                shoruk: dayData.sunrise[0],
                dhuhr: dayData.dhuhr[0],
                asr: dayData.asr[0],
                maghrib: dayData.maghrib[0],
                isha: dayData.isha[0],
              },
            };
          }
        }

        throw new Error("Kunde inte tolka XML-data");
      } catch (xmlError) {
        quietLog("Fel vid hämtning från XML-API", xmlError);

        // Prova alternativ XML-endpoint
        try {
          const alternativeXmlUrl = `${islamicBaseUrl}/stockholm/wp-content/plugins/bonetider/Bonetider_Widget_Public/includes/get_monthly_prayer_times.php?city=${cityCode}&month=${month}&year=${year}&calculation_method=mwl`;

          const alternativeResponse = await axios.get(alternativeXmlUrl, { timeout: 5000 });

          // Använd fast-xml-parser istället för xml2js
          const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: ""
          });

          const alternativePrayerTimesData = parser.parse(alternativeResponse.data as string) as PrayerTimesXmlResponse;

          if (
            alternativePrayerTimesData &&
            alternativePrayerTimesData.prayer_times &&
            alternativePrayerTimesData.prayer_times.day
          ) {
            const dayData = alternativePrayerTimesData.prayer_times.day.find(
              (d) => parseInt(d.$.day_number) === day
            );

            if (dayData) {
              return {
                date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
                location: location,
                prayerTimes: {
                  fajr: dayData.fajr[0],
                  shoruk: dayData.sunrise[0],
                  dhuhr: dayData.dhuhr[0],
                  asr: dayData.asr[0],
                  maghrib: dayData.maghrib[0],
                  isha: dayData.isha[0],
                },
              };
            }
          }

          throw new Error("Kunde inte tolka alternativ XML-data");
        } catch (alternativeXmlError) {
          quietLog("Fel vid hämtning från alternativ XML-endpoint", alternativeXmlError);

          // Som sista utväg, använd Aladhan API
          try {
            return await fallbackToAladhanAPI(location, currentDate);
          } catch (aladhanError) {
            quietLog("Fel vid hämtning från Aladhan API", aladhanError);

            // Throw a properly formatted error
            throw new Error("Kunde inte hämta bönetider från någon källa");
          }
        }
      }
    }
  }
};

// Funktion som använder Aladhan API som sista utväg
const fallbackToAladhanAPI = async (
  location: string,
  date: Date
): Promise<PrayerTimesResponse> => {
  // Extrahera ungefärliga koordinater baserat på stad
  let latitude = 59.3293; // Default Stockholm
  let longitude = 18.0686;

  const cityName = location.split(",")[0].trim().toLowerCase();

  // Enkla koordinater för större svenska städer
  if (cityName.includes("göteborg")) {
    latitude = 57.7089;
    longitude = 11.9746;
  } else if (cityName.includes("malmö")) {
    latitude = 55.6050;
    longitude = 13.0038;
  } else if (cityName.includes("uppsala")) {
    latitude = 59.8586;
    longitude = 17.6389;
  }

  const dateString = date.toISOString().split('T')[0];
  return await fetchPrayerTimes(latitude, longitude, dateString);
};

// Function to get the next prayer time
const getNextPrayer = (
  prayerTimes: PrayerTimesResponse,
): { name: string; time: string } => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  const prayers = [
    { name: "Fajr", time: prayerTimes.prayerTimes.fajr },
    { name: "Dhuhr", time: prayerTimes.prayerTimes.dhuhr },
    { name: "Asr", time: prayerTimes.prayerTimes.asr },
    { name: "Maghrib", time: prayerTimes.prayerTimes.maghrib },
    { name: "Isha", time: prayerTimes.prayerTimes.isha },
  ];

  for (const prayer of prayers) {
    const [prayerHour, prayerMinute] = prayer.time.split(":").map(Number);

    if (
      prayerHour > currentHour ||
      (prayerHour === currentHour && prayerMinute > currentMinute)
    ) {
      return prayer;
    }
  }

  // If all prayers for today have passed, return Fajr for tomorrow
  return { name: "Fajr (imorgon)", time: prayers[0].time };
};

// Add this function to your prayerTimesAPI.ts file
export function calculateProgress(prayerTimesData: PrayerTimesResponse): number {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  // Skapa en array med bönetider i ordning
  const prayerTimesArray = [
    { name: "fajr", time: prayerTimesData.prayerTimes.fajr },
    { name: "dhuhr", time: prayerTimesData.prayerTimes.dhuhr },
    { name: "asr", time: prayerTimesData.prayerTimes.asr },
    { name: "maghrib", time: prayerTimesData.prayerTimes.maghrib },
    { name: "isha", time: prayerTimesData.prayerTimes.isha },
  ];

  // Lägg till morgondagens Fajr för att hantera tiden efter Isha
  const nextDayFajr = {
    name: "fajr_tomorrow",
    time: prayerTimesData.nextDayPrayerTimes?.fajr || prayerTimesData.prayerTimes.fajr
  };
  prayerTimesArray.push(nextDayFajr);

  // Konvertera alla bönetider till minuter
  const prayerTimesInMinutes = prayerTimesArray.map(prayer => {
    const [hours, minutes] = prayer.time.split(":").map(Number);
    let timeInMinutes = hours * 60 + minutes;

    // Om det är morgondagens Fajr, lägg till 24 timmar
    if (prayer.name === "fajr_tomorrow") {
      timeInMinutes += 24 * 60;
    }

    return {
      name: prayer.name,
      timeInMinutes: timeInMinutes
    };
  });

  // Hitta nuvarande och nästa bön
  let currentPrayer = null;
  let nextPrayer = null;

  // Hitta nuvarande böneintervall
  for (let i = 0; i < prayerTimesInMinutes.length - 1; i++) {
    if (
      currentTimeInMinutes >= prayerTimesInMinutes[i].timeInMinutes &&
      currentTimeInMinutes < prayerTimesInMinutes[i + 1].timeInMinutes
    ) {
      currentPrayer = prayerTimesInMinutes[i];
      nextPrayer = prayerTimesInMinutes[i + 1];
      break;
    }
  }

  // Om vi inte hittade något intervall, är vi antingen före dagens Fajr eller efter Isha
  if (!currentPrayer || !nextPrayer) {
    // Om vi är före Fajr
    if (currentTimeInMinutes < prayerTimesInMinutes[0].timeInMinutes) {
      // Använd tiden från midnatt (00:00) till Fajr
      currentPrayer = { name: "midnight", timeInMinutes: 0 };
      nextPrayer = prayerTimesInMinutes[0];
    }
    // Om vi är efter Isha, använd Isha till morgondagens Fajr
    else {
      currentPrayer = prayerTimesInMinutes[4]; // Isha
      nextPrayer = prayerTimesInMinutes[5];    // Fajr imorgon
    }
  }

  // Beräkna framsteg mellan nuvarande och nästa bön
  const totalDuration = nextPrayer.timeInMinutes - currentPrayer.timeInMinutes;
  const elapsedTime = currentTimeInMinutes - currentPrayer.timeInMinutes;
  const progress = (elapsedTime / totalDuration) * 100;

  return Math.min(Math.max(progress, 0), 100);
}

export { getPrayerTimes, getNextPrayer };
export type { PrayerTime }; // Remove PrayerTimesResponse from here since it's already exported above

// ===== VAKTIJA.EU INTEGRATION =====

// Fetch prayer times from Vaktija.eu (Bosnian Islamic Community)
const getVaktijaPrayerTimes = async (
  location: string,
  dateStr?: string,
): Promise<PrayerTimesResponse> => {
  const currentDate = dateStr ? new Date(dateStr) : new Date();
  const day = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const cityName = location.split(",")[0].trim();
  const citySlug = vaktijaCityMapping[cityName] || "stockholm";

  try {
    // Vaktija.eu is a Next.js app that embeds prayer times data in __NEXT_DATA__
    const url = `${vaktijaBaseUrl}/sv/${citySlug}`;
    const response = await axios.get(url, { timeout: 10000 });
    const html = response.data as string;

    // Extract __NEXT_DATA__ JSON from the HTML - use a robust regex that handles large/multiline JSON
    const nextDataMatch = html.match(/<script id="__NEXT_DATA__" type="application\/json">([\s\S]+?)<\/script>/);
    if (!nextDataMatch || !nextDataMatch[1]) {
      throw new Error("Could not find __NEXT_DATA__ in vaktija.eu response");
    }

    const nextData = JSON.parse(nextDataMatch[1]);
    const dailyPrayers = nextData?.props?.pageProps?.dailyPrayersRes;

    if (!dailyPrayers) {
      throw new Error("Could not parse prayer times from vaktija.eu");
    }

    // Vaktija returns times as "HH:MM:SS", we need "HH:MM"
    const formatTime = (time: string): string => {
      const parts = time.split(":");
      return `${parts[0]}:${parts[1]}`;
    };

    return {
      date: `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
      location: location,
      prayerTimes: {
        fajr: formatTime(dailyPrayers.fajr),
        sunrise: formatTime(dailyPrayers.sunrise),
        shoruk: formatTime(dailyPrayers.sunrise),
        dhuhr: formatTime(dailyPrayers.dhuhr),
        asr: formatTime(dailyPrayers.asr),
        maghrib: formatTime(dailyPrayers.maghrib),
        isha: formatTime(dailyPrayers.isha),
      },
    };
  } catch (error) {
    quietLog("Error fetching from vaktija.eu", error);
    throw error;
  }
};

// ===== SOURCE ROUTING =====

// Coordinates for Swedish cities (used by Aladhan API)
const cityCoordinates: Record<string, { lat: number; lon: number }> = {
  "Stockholm": { lat: 59.3293, lon: 18.0686 },
  "Göteborg": { lat: 57.7089, lon: 11.9746 },
  "Malmö": { lat: 55.6050, lon: 13.0038 },
  "Uppsala": { lat: 59.8586, lon: 17.6389 },
  "Västerås": { lat: 59.6099, lon: 16.5448 },
  "Örebro": { lat: 59.2753, lon: 15.2134 },
  "Linköping": { lat: 58.4108, lon: 15.6214 },
  "Helsingborg": { lat: 56.0465, lon: 12.6945 },
  "Jönköping": { lat: 57.7826, lon: 14.1618 },
  "Norrköping": { lat: 58.5877, lon: 16.1924 },
};

// Main function: fetch prayer times based on selected source
export const getPrayerTimesBySource = async (
  source: PrayerSource,
  location: string,
  dateStr?: string,
): Promise<PrayerTimesResponse> => {
  try {
    console.log(`[PrayerAPI] Fetching times from source: ${source}, location: ${location}`);
    switch (source) {
      case "islamiska":
        return await getPrayerTimes(location, dateStr);

      case "vaktija":
        return await getVaktijaPrayerTimes(location, dateStr);

      case "aladhan": {
        const cityName = location.split(",")[0].trim();
        const coords = cityCoordinates[cityName] || { lat: 59.3293, lon: 18.0686 };
        const dateString = dateStr || new Date().toISOString().split("T")[0];
        return await fetchPrayerTimes(coords.lat, coords.lon, dateString);
      }

      default:
        return await getPrayerTimes(location, dateStr);
    }
  } catch (error) {
    quietLog(`Error fetching from source '${source}', falling back to Aladhan`, error);
    // Fallback: try Aladhan API
    try {
      const cityName = location.split(",")[0].trim();
      const coords = cityCoordinates[cityName] || { lat: 59.3293, lon: 18.0686 };
      const dateString = dateStr || new Date().toISOString().split("T")[0];
      return await fetchPrayerTimes(coords.lat, coords.lon, dateString);
    } catch (fallbackError) {
      quietLog("Fallback to Aladhan also failed", fallbackError);
      throw fallbackError;
    }
  }
};

// Update the fetchPrayerTimes function to accept a date parameter
export const fetchPrayerTimes = async (
  latitude: number,
  longitude: number,
  date?: string
): Promise<PrayerTimesResponse> => {
  try {
    // Aladhan API expects date in DD-MM-YYYY format
    let targetDate: string;
    if (date) {
      // Convert YYYY-MM-DD to DD-MM-YYYY if needed
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = date.split('-');
        targetDate = `${d}-${m}-${y}`;
      } else {
        targetDate = date;
      }
    } else {
      const now = new Date();
      targetDate = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`;
    }
    const url = `${aladhanBaseUrl}/v1/timings/${targetDate}?latitude=${latitude}&longitude=${longitude}&method=3`;

    const response = await axios.get<AladhanApiResponse>(url);
    const data = response.data;

    if (data.code !== 200) {
      throw new Error(`API error: ${data.status}`);
    }

    const timings = data.data.timings;

    return {
      prayerTimes: {
        fajr: timings.Fajr,
        sunrise: timings.Sunrise,
        dhuhr: timings.Dhuhr,
        asr: timings.Asr,
        maghrib: timings.Maghrib,
        isha: timings.Isha,
        midnight: timings.Midnight,
      },
      date: data.data.date.readable,
      location: `${latitude}, ${longitude}`,
    };
  } catch (error) {
    quietLog("Error fetching prayer times", error);
    throw error;
  }
};